import { useMemo } from "react";

/**
 * Represents either a group header row or a data item row in a grouped DataGrid.
 */
export type GroupedRow<TItem> =
    | { type: "header"; groupName: string; count: number; isCollapsed?: boolean }
    | { type: "item"; item: TItem };

export interface UseDataGridGroupsOptions<TItem> {
    /** Extracts the group name from an item. */
    getGroupName: (item: TItem) => string;
    /** Optional ordered list of group names. Groups not in the list appear at the end. */
    groupOrder?: string[];
    /** Whether to show groups that have no items (only works with groupOrder). */
    showEmpty?: boolean;
    /** Return true if a group should start collapsed. */
    isCollapsed?: (groupName: string) => boolean;
}

/**
 * Groups items into a flat array of header + item rows for rendering in a v9 DataGrid.
 *
 * Usage:
 * ```tsx
 * const { groupedRows } = useDataGridGroups(items, {
 *   getGroupName: item => item.category,
 *   groupOrder: ["Active", "Inactive"],
 * });
 * // Render groupedRows in DataGridBody, with group headers as special styled rows
 * ```
 */
export function useDataGridGroups<TItem>(
    items: TItem[],
    options: UseDataGridGroupsOptions<TItem>
): { groupedRows: GroupedRow<TItem>[] } {
    const { getGroupName, groupOrder, showEmpty, isCollapsed } = options;

    const groupedRows = useMemo(() => {
        const itemsByGroup: Record<string, TItem[]> = {};

        // Pre-populate groups from groupOrder to preserve order
        groupOrder?.forEach(name => {
            itemsByGroup[name] = [];
        });

        // Bin items into groups
        items.forEach(item => {
            const name = getGroupName(item);
            if (!itemsByGroup[name]) {
                itemsByGroup[name] = [];
            }
            itemsByGroup[name].push(item);
        });

        const orderedGroupNames = groupOrder || Object.keys(itemsByGroup);
        const rows: GroupedRow<TItem>[] = [];

        for (const groupName of orderedGroupNames) {
            const groupItems = itemsByGroup[groupName];
            if (!groupItems) continue;
            if (!showEmpty && groupItems.length === 0) continue;

            const collapsed = isCollapsed?.(groupName);
            rows.push({ type: "header", groupName, count: groupItems.length, isCollapsed: collapsed });

            if (!collapsed) {
                for (const item of groupItems) {
                    rows.push({ type: "item", item });
                }
            }
        }

        return rows;
    }, [items, getGroupName, groupOrder, showEmpty, isCollapsed]);

    return { groupedRows };
}
